import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { connectSocket, disconnectSocket } from '../api/socket';

interface User {
  id: string;
  name: string;
  role: 'AUCTIONEER' | 'BIDDER' | 'SUPERADMIN';
}

interface AuthContextType {
  user: User | null;
  token: string | null;
  login: (token: string, user: User) => void;
  logout: () => void;
}

const AuthContext = createContext<AuthContextType | null>(null);

// Decode a JWT's `exp` claim (seconds since epoch) without a library, so an expired token
// is never restored into a session. Returns true for anything unparseable — a malformed
// token should be treated as dead rather than trusted. The backend issues a 24-hour TTL
// (routes/auth.py), so this fires roughly daily.
function isTokenExpired(token: string): boolean {
  try {
    const part = token.split('.')[1];
    const base64 = part
      .replace(/-/g, '+')
      .replace(/_/g, '/')
      .padEnd(Math.ceil(part.length / 4) * 4, '=');
    const payload = JSON.parse(atob(base64));
    if (typeof payload.exp !== 'number') return false;
    return payload.exp * 1000 <= Date.now();
  } catch {
    return true;
  }
}

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [token, setToken] = useState<string | null>(null);

  const clearSession = useCallback(() => {
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    setToken(null);
    setUser(null);
    disconnectSocket();
  }, []);

  useEffect(() => {
    const storedToken = localStorage.getItem('token');
    const storedUser = localStorage.getItem('user');
    if (storedToken && storedUser && !isTokenExpired(storedToken)) {
      const parsed = JSON.parse(storedUser);
      setToken(storedToken);
      setUser(parsed);
      // Superadmins use only the user-management page; they need no auction socket.
      if (parsed?.role !== 'SUPERADMIN') connectSocket(storedToken);
    } else if (storedToken || storedUser) {
      // Expired, malformed, or half-written session — clear it so the app starts at
      // the login page instead of firing a doomed request with a dead token.
      clearSession();
    }

    // api/client.ts dispatches this when a protected request returns 401 (the token
    // died mid-session). Dropping the session flips `user` to null, and ProtectedRoute
    // redirects to the login page rather than the app crashing on the rejected request.
    window.addEventListener('auth:unauthorized', clearSession);
    return () => window.removeEventListener('auth:unauthorized', clearSession);
  }, [clearSession]);

  const login = useCallback((token: string, user: User) => {
    localStorage.setItem('token', token);
    localStorage.setItem('user', JSON.stringify(user));
    setToken(token);
    setUser(user);
    if (user.role !== 'SUPERADMIN') connectSocket(token);
  }, []);

  return (
    <AuthContext.Provider value={{ user, token, login, logout: clearSession }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used within AuthProvider');
  return ctx;
}
